/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { createAsyncThunk, AsyncThunk } from "@reduxjs/toolkit";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { apiPersistCache } from "hooks/use-api-persist-cache";
import { isArrayNotEmpty, isStringNotEmpty, isTrue } from "utils/empty-exists";
import { fetchModel } from "../model";
import { Logger } from "cms/utils/log/logger";

export const fetchWebpage = createAsyncThunk(
    "webpage/fetch",

    /**
     * @param {object} arg
     * @param thunkAPI
     * @param {string} arg.token
     * @param {string} arg.webpageGuid
     */
    async function (arg, thunkAPI) {
        const { token, webpageGuid } = arg;
        const { dispatch } = thunkAPI;

        // TODO: combine webpage, parents, children into one call in node api
        const [currentWebpage, childrenResp, parents] = await Promise.all([
            SimpliCityAdminSDK.webpage.findOne(token, webpageGuid),
            SimpliCityAdminSDK.webpage.findChildren(token, webpageGuid),
            SimpliCityAdminSDK.webpage.findParents(token, webpageGuid),
        ]);

        currentWebpage.children = childrenResp?.children;
        currentWebpage.parents = parents;
        if (isArrayNotEmpty(parents)) {
            const _parent = parents[1]; // 0 = self, 1 = parent
            const parentGuid = _parent?.guid;
            // TODO: Need a findParent to replace parent code that follows
            if (isStringNotEmpty(parentGuid)) {
                const parent = await SimpliCityAdminSDK.webpage.findOne(token, parentGuid);
                currentWebpage.parent = parent;
            }
        }

        // this is not generally recommended, but in this case, we want to load the model
        // before we finish loading the webpage so we can use the loading property
        if (currentWebpage.model) {
            await dispatch(fetchModel({ token, id: currentWebpage.model }));
        } else {
            Logger.warn("no model for webpage", currentWebpage?.model);
        }

        return currentWebpage;
        // see webpage.slice extraReducers for state update code
    }
);

export const refreshWebpage = createAsyncThunk(
    "webpage/refresh",
    
    /**
     * @param {object} arg
     * @param thunkAPI
     * @param {string} arg.token
     * @param {string} arg.webpageGuid
     */
    async function (arg, thunkAPI) {
        const { token, webpageGuid } = arg;
        const { dispatch } = thunkAPI;
        const currentWebpage = await SimpliCityAdminSDK.webpage.findOne(token, webpageGuid);
        if (currentWebpage.model) {
            await dispatch(fetchModel({ token, id: currentWebpage.model }));
        }
        return currentWebpage;
        // see webpage.slice extraReducers for state update code
    }
);


export const fetchPublishStatus = createAsyncThunk(
    "webpage/fetchPublishStatus",


    /**
     * @param {object} arg
     * @param thunkAPI
     */
    async function (arg, thunkAPI) {
        /** @type {RootState} */
        const { webpage } = thunkAPI.getState();
        // TODO: Need to describe what is apiPersistCache for? (token is not hard to locate)
        const token = apiPersistCache?.token;
        const guid = webpage?.current?.guid;
        const website = webpage?.current?.website;

        /** @type { import('./webpage.types').PublishStatus} */
        const publishStatus = await SimpliCityAdminSDK.publishing.getStatus(
            token,
            website,
            guid
        );
        // const status_msg = `[webpage.thunks] fetchPublishStatus ${guid} ${JSON.stringify(publishStatus)}`;
        // Logger.debug(status_msg, { publishStatus });

        return { publishStatus };
        // see webpage.slice extraReducers for state update code
    }
);

/**
 * @param queue
 * @param {import('./webpage.types').PublishStatus['change_type']} changeType
 * @param {RootState} thunkAPI
 * @param notify
 */
const addCurrentPage = async (queue, changeType, thunkAPI, notify) => {
    /** @type {RootState} */
    const state = thunkAPI.getState();
    const webpage = state?.webpage;
    const settings = state?.orgSettings?.all;
    const token = apiPersistCache?.token;
    const guid = webpage?.current?.guid;
    const website = webpage?.current?.website;
    // disable feature flag
    const featureEnabled = isTrue(settings?.feature_notify_publish) ?? false;
    if (!featureEnabled) {
        notify.notify_enabled = false;
        notify.renotify_enabled = false;
        notify.notify_message = null;
    }
    const payload = {
        resource_guid: guid,
        resource_type: "webpage",
        change_type: changeType,
        notify_enabled: notify?.notify_enabled,
        renotify_enabled: notify?.renotify_enabled,
        notify_message: notify?.notify_message
    };
    // Logger.debug(`[webpage.thunks] add to queue [queue:${queue}] [resource_guid:${guid}] [payload:${JSON.stringify(payload)}]`);
    const status = await SimpliCityAdminSDK.publishing.add(token, website, queue, payload);
    return status;
};

/**
 * @param queue
 * @param {RootState} thunkAPI
 */
const removeCurrentPage = async (queue, thunkAPI) =>   {
    /** @type {RootState} */
    const { webpage } = thunkAPI.getState();
    const { token } = apiPersistCache;
    const { guid, website } = webpage.current || {};
    // Validator should allow this full object
    const payload = {
        resource_guid: guid,
    };
    // Logger.debug(`[webpage.thunks] remove from publishing queue [resource_guid:${guid}] [payload:${JSON.stringify(payload)}]`);
    const status = await SimpliCityAdminSDK.publishing.remove(token, website, queue, payload);
    return status;
};

/**
 * @param queue
 * @param {{
 *   resource_guid: string,
 *   resource_type: string,
 *   change_type: string,
 * }} payload
 * @param {RootState} thunkAPI
 */
const addResource = async (queue, payload, thunkAPI) => {
    /** @type {RootState} */
    const { website } = thunkAPI.getState();
    const { current_guid: website_guid } = website;
    const { token } = apiPersistCache;
    await SimpliCityAdminSDK.publishing.add(token, website_guid, queue, payload);
};

/**
 * @param queue
 * @param {{
 *   resource_guid: string,
 *   resource_type: string,
 *   change_type: string,
 * }} payload
 * @param {RootState} thunkAPI
 */
const removeResource = async (queue, payload, thunkAPI) => {
    /** @type {RootState} */
    const { website } = thunkAPI.getState();
    const { current_guid: website_guid } = website;
    const { token } = apiPersistCache;
    await SimpliCityAdminSDK.publishing.remove(token, website_guid, queue, payload);
};

const moveResource = async (resource_guid, fromQueue, toQueue, thunkAPI) => {
    /** @type {RootState} */
    const { website } = thunkAPI.getState();
    const { current_guid: website_guid } = website;
    const { token } = apiPersistCache;
    await SimpliCityAdminSDK.publishing.moveResourceToQueue(token, website_guid, resource_guid, fromQueue, toQueue);
};

export const addToReviewQueue = createAsyncThunk(
    "webpage/addToReviewQueue",
    async function (arg, thunkAPI) {
        // Logger.debug("webpage/addToReviewQueue");
        await addCurrentPage("review", "add", thunkAPI, arg);
        return thunkAPI.dispatch(fetchPublishStatus());
    }
);

export const unpublish = createAsyncThunk(
    "webpage/unpublish",
    async function (notify, thunkAPI) {
        Logger.debug("webpage/unpublish");
        // const notify = arg?.notify;
        await removeCurrentPage("review", thunkAPI);
        await removeCurrentPage("main", thunkAPI);
        await addCurrentPage("main", "remove", thunkAPI, notify);
        return thunkAPI.dispatch(fetchPublishStatus());
    }
);

export const cancelReview = createAsyncThunk(
    "webpage/cancelReview",
    async function (arg, thunkAPI) {
        Logger.debug("webpage/cancelReview");
        await removeCurrentPage("review", thunkAPI);
        return thunkAPI.dispatch(fetchPublishStatus());
    }
);

export const cancelPublish = createAsyncThunk(
    "webpage/cancelPublish",
    async function (arg, thunkAPI) {
        Logger.debug("webpage/cancelPublish");
        await removeCurrentPage("main", thunkAPI);
        return thunkAPI.dispatch(fetchPublishStatus());
    }
);

export const addResourceToPublishQueue = createAsyncThunk(
    "webpage/addResourceToPublishQueue",
    async function (payload, thunkAPI) {
        await addResource("main", payload, thunkAPI);
        return thunkAPI.dispatch(fetchPublishStatus());
    }
);

export const cancelAllQueues = createAsyncThunk(
    "webpage/cancelQueue",
    async function (payload, thunkAPI) {
        await removeResource("review", payload, thunkAPI);
        await removeResource("main", payload, thunkAPI);
        return thunkAPI.dispatch(fetchPublishStatus());
    }
);

export const approveResource = createAsyncThunk(
    "webpage/approveResource",
    async function (resource_guid, thunkAPI) {
        await moveResource(resource_guid, "review", "main", thunkAPI);
        return thunkAPI.dispatch(fetchPublishStatus());
    }
);

/**
 * @typedef {object} RootState
 * @property {import('./webpage.state').WebpageState} webpage
 */
